import {
  APIClient,
  UnAuthorizationAPIError,
  getCookie,
  resolveAPICall,
  usePrevioCore,
  type APIRequest,
  type APISuccess,
  type APICallArgs,
  type APICallOptions,
  type APICallResult,
  type APIDataOnlyCallArgs,
  type APIFullResponseCallArgs,
} from '@previo/core'
import { Environment } from '~/Environment'
import { APIPaths } from '~/http/APIPaths'
import { AuthRecovery } from '~/http/AuthRecovery'
import { useAuthenticationStore } from '~/store/authentication'

type CRMResponseFields = object

type CRMResponse<TResponseData> = APISuccess<TResponseData, CRMResponseFields>

type CallArgs<TOptions extends APICallOptions = APICallOptions, TBody = unknown> = APICallArgs<
  APIPaths,
  TBody,
  TOptions
>

type CallResult<TResponseData, TOptions extends APICallOptions> = APICallResult<
  TResponseData,
  CRMResponseFields,
  TOptions
>

const { useLocale } = usePrevioCore()

const apiClient = new APIClient<CRMResponseFields>(import.meta.env.CRM_FE_API_URL)
const authRecovery = new AuthRecovery(apiClient)

export default class Http {
  protected async callNoAuthAPI<TBody, TResponseData>(
    ...args: CallArgs<APIDataOnlyCallArgs, TBody>
  ): Promise<TResponseData>

  protected async callNoAuthAPI<TBody, TResponseData>(
    ...args: CallArgs<APIFullResponseCallArgs, TBody>
  ): Promise<CRMResponse<TResponseData>>

  protected async callNoAuthAPI<TBody, TResponseData, TOptions extends APICallOptions>(
    ...args: CallArgs<TOptions, TBody>
  ): Promise<CallResult<TResponseData, TOptions>> {
    return resolveAPICall<TResponseData, APIPaths, CRMResponseFields, TOptions>(args, (request) =>
      this.call<TResponseData>(request),
    )
  }

  protected async callAuthAPI<TBody, TResponseData>(
    ...args: CallArgs<APIDataOnlyCallArgs, TBody>
  ): Promise<TResponseData>

  protected async callAuthAPI<TBody, TResponseData>(
    ...args: CallArgs<APIFullResponseCallArgs, TBody>
  ): Promise<CRMResponse<TResponseData>>

  protected async callAuthAPI<TBody, TResponseData, TOptions extends APICallOptions>(
    ...args: CallArgs<TOptions, TBody>
  ): Promise<CallResult<TResponseData, TOptions>> {
    return resolveAPICall<TResponseData, APIPaths, CRMResponseFields, TOptions>(args, (request) =>
      this.callWithAuth<TResponseData>(request),
    )
  }

  private async callWithAuth<TResponseData>(
    request: APIRequest,
  ): Promise<CRMResponse<TResponseData>> {
    const authorization = useAuthenticationStore.value

    if (!authorization) {
      throw new Error('Authorization token is not provided from local storage')
    }

    request.headers['Authorization'] = `Bearer ${authorization.accessToken}`

    return this.call<TResponseData>(request).catch(async (e) => {
      if (e instanceof UnAuthorizationAPIError) {
        return authRecovery.recoverAndThen(authorization.accessToken, () =>
          this.callWithAuth<TResponseData>(request),
        )
      }

      throw e
    })
  }

  private async call<TResponseData>(request: APIRequest): Promise<CRMResponse<TResponseData>> {
    if (Environment.isEnvLikeDevelopment()) {
      const xdebugSession = getCookie('XDEBUG_SESSION')

      if (xdebugSession) {
        request.query['XDEBUG_SESSION'] = xdebugSession
      }
    }

    request.headers['Accept'] ??= 'application/json'
    request.headers['Accept-Language'] ??= useLocale().get()

    return apiClient.call<TResponseData>(request)
  }
}
